import { CheckCircle, XCircle } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const ProcessModal = ({
  t,
  styles,
  isRTL,
  selectedRequest,
  formData,
  setFormData,
  getEmployeeName,
  formatCurrency,
  onApprove,
  onReject,
  onDisburse,
  onClose,
  isProcessing = false,
}) => {
  const isDisbursing = formData.status === "disburse";
  const { hasFeatureAccess } = useAuth();
  const normalizedStatus = String(selectedRequest?.status || "").toLowerCase();
  const canActOnRequest = Boolean(selectedRequest?.currentUserCanAct) && normalizedStatus === "pending";
  // Require BOTH: workflow-assigned to act on this request AND the admin-level feature grant.
  const canApprove = canActOnRequest && hasFeatureAccess("advancements", "approve");
  const canReject = canActOnRequest && hasFeatureAccess("advancements", "reject");
  // Disburse: request approved + workflow permits this user to disburse + feature grant
  // ('approve' still implies disburse for admin levels granted only 'approve').
  const canDisburse =
    normalizedStatus === "approved" &&
    Boolean(selectedRequest?.currentUserCanDisburse) &&
    (hasFeatureAccess("advancements", "approve") || hasFeatureAccess("advancements", "disburse"));

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.modalTitle}>
          {isDisbursing ? t.disburseSolfa : t.processSolfa}
        </h3>

        {/* Request Summary */}
        <div style={{ marginBottom: "24px" }}>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>{t.employee}:</span>
            <span style={styles.detailValue}>{getEmployeeName(selectedRequest)}</span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>{t.amount}:</span>
            <span style={styles.detailValue}>
              {formatCurrency(selectedRequest.requestedAmount)}
            </span>
          </div>
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>{t.installments}:</span>
            <span style={styles.detailValue}>
              {selectedRequest.numberOfInstallments || "N/A"}
            </span>
          </div>
        </div>

        {isDisbursing ? (
          <>
            {/* Disbursement Method */}
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.disbursementMethodLabel}</label>
              <select
                style={styles.select}
                value={formData.disbursementMethod}
                onChange={(e) =>
                  setFormData({ ...formData, disbursementMethod: e.target.value })
                }
              >
                <option value="bank_transfer">{t.bankTransfer}</option>
                <option value="cash">{t.cash}</option>
                <option value="check">{t.check}</option>
              </select>
            </div>

            {/* Reference Number */}
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.referenceNumberRequired}</label>
              <input
                type="text"
                style={styles.input}
                value={formData.referenceNumber}
                onChange={(e) =>
                  setFormData({ ...formData, referenceNumber: e.target.value })
                }
                placeholder={t.referenceNumberPlaceholder}
              />
            </div>

            <div
              style={{
                ...styles.buttonGroup,
                justifyContent: isRTL ? "flex-start" : "flex-end",
                marginTop: "24px",
              }}
            >
              <button style={styles.secondaryButton} onClick={onClose} disabled={isProcessing}>
                {t.cancel}
              </button>
              {canDisburse && (
                <button
                  style={{ ...styles.button, opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                  onClick={onDisburse}
                  disabled={isProcessing}
                >
                  {isProcessing ? t.processing || '...' : t.confirmDisbursement}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Approval Notes */}
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.approvalNotesOptional}</label>
              <textarea
                style={styles.textarea}
                value={formData.approvalNotes}
                onChange={(e) =>
                  setFormData({ ...formData, approvalNotes: e.target.value })
                }
                placeholder={t.approvalNotesPlaceholder}
              />
            </div>

            {/* Rejection Reason */}
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.rejectionReasonIfRejecting}</label>
              <textarea
                style={styles.textarea}
                value={formData.rejectionReason}
                onChange={(e) =>
                  setFormData({ ...formData, rejectionReason: e.target.value })
                }
                placeholder={t.rejectionReasonPlaceholder}
              />
            </div>

            <div
              style={{
                ...styles.buttonGroup,
                justifyContent: isRTL ? "flex-start" : "flex-end",
                marginTop: "24px",
              }}
            >
              <button style={styles.secondaryButton} onClick={onClose} disabled={isProcessing}>
                {t.cancel}
              </button>
              {canReject && (
                <button
                  style={{ ...styles.rejectButton, opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                  onClick={onReject}
                  disabled={isProcessing}
                >
                  <XCircle size={16} /> {t.reject}
                </button>
              )}
              {canApprove && (
                <button
                  style={{ ...styles.button, opacity: isProcessing ? 0.6 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                  onClick={onApprove}
                  disabled={isProcessing}
                >
                  <CheckCircle size={16} /> {t.approve}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProcessModal;